/**
 * ResearchSection - Cloud benchmark runner UI
 *
 * Allows submitting benchmark runs (Quick/Standard/Full suites)
 * and viewing results in real-time via Firestore listeners.
 */

import React, { useState, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    MenuItem,
    CircularProgress,
    Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    DeleteSweep as CleanupIcon,
} from '@mui/icons-material';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';
import { useBenchmarkRuns } from '../../../hooks';
import BenchmarkRunsTable from './BenchmarkRunsTable';
import BenchmarkRunDetail from './BenchmarkRunDetail';

const PHD_API_BASE = process.env.REACT_APP_PHD_API_URL || 'http://localhost:5002';

const SUITES = [
    { value: 'quick', label: 'Quick (2 scenarios)' },
    { value: 'standard', label: 'Standard (8 scenarios)' },
    { value: 'full', label: 'Full (24 scenarios)' },
];

export default function ResearchSection() {
    const { runs, loading } = useBenchmarkRuns({ maxResults: 50 });
    const [selectedRunId, setSelectedRunId] = useState(null);
    const [suite, setSuite] = useState('quick');
    const [label, setLabel] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [cleaning, setCleaning] = useState(false);

    const handleCleanup = useCallback(async (mode) => {
        const modeLabels = {
            stuck: 'all stuck/incomplete runs (>1h old)',
            failed: 'all failed runs',
            all: 'ALL benchmark runs',
        };
        if (!window.confirm(`Delete ${modeLabels[mode]}? This cannot be undone.`)) return;

        setCleaning(true);
        setSubmitError(null);
        try {
            const response = await fetch(`${PHD_API_BASE}/api/coordination/benchmark-runs/cleanup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ mode }),
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.error || `HTTP ${response.status}`);
            }
            const data = await response.json();
            console.log(`[ResearchSection] Cleaned up ${data.deleted} runs (mode=${mode})`);
        } catch (err) {
            console.error('[ResearchSection] Cleanup failed:', err);
            setSubmitError(err.message);
        } finally {
            setCleaning(false);
        }
    }, []);

    const handleRunBenchmark = async () => {
        setSubmitting(true);
        setSubmitError(null);

        try {
            const body = { suite };
            if (label.trim()) {
                body.label = label.trim();
            }

            const response = await fetch(`${PHD_API_BASE}/api/coordination/run-benchmark`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.error || `HTTP ${response.status}`);
            }

            const data = await response.json();
            // Auto-select the new run to show detail
            if (data.run_id) {
                setSelectedRunId(data.run_id);
            }
            setLabel('');
        } catch (err) {
            console.error('[ResearchSection] Benchmark submission failed:', err);
            setSubmitError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    // Detail view
    if (selectedRunId) {
        return (
            <BenchmarkRunDetail
                runId={selectedRunId}
                onBack={() => setSelectedRunId(null)}
            />
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Typography sx={{
                fontFamily: vortxTypography.heading,
                fontSize: '1.1rem',
                fontWeight: 700,
                color: '#9b59b6',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                mb: 3,
            }}>
                Benchmark Runner
            </Typography>

            {/* Controls row */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 3,
                flexWrap: 'wrap',
            }}>
                <TextField
                    select
                    size="small"
                    label="Suite"
                    value={suite}
                    onChange={(e) => setSuite(e.target.value)}
                    sx={{
                        minWidth: 200,
                        '& .MuiInputBase-root': {
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.8rem',
                        },
                    }}
                >
                    {SUITES.map((s) => (
                        <MenuItem key={s.value} value={s.value} sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.8rem',
                        }}>
                            {s.label}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    size="small"
                    label="Label (optional)"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="e.g. ADMM v2.1 test"
                    sx={{
                        minWidth: 220,
                        '& .MuiInputBase-root': {
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.8rem',
                        },
                    }}
                />

                <Button
                    variant="contained"
                    onClick={handleRunBenchmark}
                    disabled={submitting}
                    sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        bgcolor: '#9b59b6',
                        color: '#fff',
                        px: 3,
                        '&:hover': { bgcolor: '#8e44ad' },
                        '&.Mui-disabled': { opacity: 0.5 },
                    }}
                >
                    {submitting ? (
                        <CircularProgress size={16} sx={{ color: '#000', mr: 1 }} />
                    ) : null}
                    Run Benchmark
                </Button>

                {/* Spacer pushes cleanup to right */}
                <Box sx={{ flex: 1 }} />

                <Button
                    variant="outlined"
                    size="small"
                    startIcon={cleaning ? <CircularProgress size={14} sx={{ color: '#9b59b6' }} /> : <CleanupIcon />}
                    onClick={() => handleCleanup('all')}
                    disabled={cleaning}
                    sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        color: '#888',
                        borderColor: 'rgba(136, 136, 136, 0.3)',
                        '&:hover': {
                            color: vortxColors.error,
                            borderColor: alpha(vortxColors.error, 0.5),
                            bgcolor: alpha(vortxColors.error, 0.05),
                        },
                    }}
                >
                    Delete All Runs
                </Button>
            </Box>

            {submitError && (
                <Alert
                    severity="error"
                    onClose={() => setSubmitError(null)}
                    sx={{ mb: 2, fontFamily: vortxTypography.mono, fontSize: '0.8rem' }}
                >
                    {submitError}
                </Alert>
            )}

            {/* Runs table */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={28} sx={{ color: '#9b59b6' }} />
                </Box>
            ) : (
                <BenchmarkRunsTable
                    runs={runs}
                    onSelectRun={setSelectedRunId}
                />
            )}
        </Box>
    );
}
